import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, UploadCloud, Users, X } from 'lucide-react'
import {
  listClients,
  listEmployees,
  listTasks,
  createClient,
  createContract,
  createContact,
  createTask,
  createAssignment,
  createEmployee,
  type Employee,
} from '../../../lib/api'
import { refreshRoster } from '../../../lib/roster'
import { parseFile, parseDelimited } from '../intake/parse'
import type { ParsedTable } from '../intake/types'
import { parseWide, type OnboardParse } from './parseWide'
import { matchEmployee } from '../../team/matchEmployee'

const ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm,.xlsb,.xls,.ods'
const COLORS = ['#00D1FF', '#3FB68B', '#E0A93B', '#7C9CFF', '#E5484D', '#F2A0C4']
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
const normTitle = (t: string) => t.trim().toLowerCase().replace(/\s+/g, ' ')
const taskKey = (clientId: string, title: string) => `${clientId}::${normTitle(title)}`
const emailValid = (e: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length > 1) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

type Resolution =
  | { mode: 'match'; employeeId: string }
  | { mode: 'create'; email: string; initials: string; color: string }
  | { mode: 'skip' }

interface RunResult {
  clients: number
  reusedClients: number
  employees: number
  assignments: number
  tasks: number
  tasksSkipped: number
  contracts: number
  contacts: number
  errors: string[]
}

interface Props {
  onClose: () => void
  onSuccess?: (message: string) => void
}

export function ClientOnboardImport({ onClose, onSuccess }: Props) {
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [table, setTable] = useState<ParsedTable | null>(null)
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>({})
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState('')
  const [result, setResult] = useState<RunResult | null>(null)

  const { data: employeesData } = useQuery({ queryKey: ['employees'], queryFn: () => listEmployees() })
  const { data: clientsData } = useQuery({ queryKey: ['clients', {}], queryFn: () => listClients() })
  const { data: tasksData } = useQuery({ queryKey: ['tasks', {}], queryFn: () => listTasks() })
  const employees = employeesData?.items ?? []

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && !running && onClose()
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, running])

  const parse: OnboardParse | null = useMemo(() => (table ? parseWide(table) : null), [table])

  // Seed resolutions once people are known.
  useEffect(() => {
    if (!parse) return
    setResolutions((prev) => {
      const next = { ...prev }
      for (const name of parse.people) {
        if (next[name]) continue
        const emp = matchEmployee(name, employees)
        next[name] = emp
          ? { mode: 'match', employeeId: emp.id }
          : { mode: 'create', email: '', initials: initialsOf(name), color: COLORS[parse.people.indexOf(name) % COLORS.length]! }
      }
      return next
    })
  }, [parse, employees])

  const setFile = async (file: File) => {
    setError(null)
    try {
      const t = await parseFile(file, { sheetHint: /client/i })
      if (!t || t.rows.length === 0) setError('No data rows found — need a header row plus records.')
      else { setTable(t); setStep(2) }
    } catch (err) {
      setError(err instanceof Error ? `Could not read the file: ${err.message}` : 'Could not read the file.')
    }
  }

  const parsePasted = () => {
    const t = parseDelimited(text)
    if (!t || t.rows.length === 0) setError('Paste a header row and at least one client row.')
    else { setTable(t); setStep(2); setError(null) }
  }

  // ── Plan (counts) ──
  const plan = useMemo(() => {
    if (!parse) return null
    const clientByName = new Map((clientsData?.items ?? []).map((c) => [norm(c.name), c.id]))
    const existing = new Set(clientByName.keys())
    // Existing tasks keyed by client::title → lets us predict which rows are dupes.
    const existingTaskKeys = new Set<string>()
    for (const t of tasksData?.items ?? []) if (t.clientId) existingTaskKeys.add(taskKey(t.clientId, t.title))

    const resolvable = (name: string) => {
      const r = resolutions[name]
      return r && r.mode !== 'skip'
    }
    let assignments = 0
    let taskAssignments = 0
    let newTasks = 0
    let dupTasks = 0
    for (const rec of parse.records) {
      assignments += rec.team.filter((t) => resolvable(t.name)).length
      const cid = rec.client.name ? clientByName.get(norm(rec.client.name)) : undefined
      for (const tk of rec.tasks) {
        const isDup = !!cid && existingTaskKeys.has(taskKey(cid, tk.title))
        if (isDup) dupTasks++
        else {
          newTasks++
          if (tk.assignedTo && resolvable(tk.assignedTo)) taskAssignments++
        }
      }
    }
    // Count clients by DISTINCT name — a name repeated in the file merges into
    // one client (second row's tasks/team attach to the first), so surface it.
    const seenNames = new Set<string>()
    let newClients = 0
    let reusedCount = 0
    let dupRows = 0
    for (const r of parse.records) {
      if (!r.client.name) continue
      const n = norm(r.client.name)
      if (existing.has(n)) reusedCount++
      else if (seenNames.has(n)) dupRows++
      else {
        seenNames.add(n)
        newClients++
      }
    }
    return {
      newClients,
      reused: reusedCount,
      dupRows,
      newEmployees: Object.values(resolutions).filter((r) => r.mode === 'create').length,
      contracts: parse.records.filter((r) => r.contract).length,
      contacts: parse.records.filter((r) => r.poc).length,
      tasks: newTasks,
      dupTasks,
      assignments,
      taskAssignments,
      skipped: Object.entries(resolutions).filter(([, r]) => r.mode === 'skip').map(([n]) => n),
    }
  }, [parse, resolutions, clientsData, tasksData])

  const createInvalid = Object.values(resolutions).some(
    (r) => r.mode === 'create' && (!emailValid(r.email) || !r.initials.trim()),
  )

  // ── Run ──
  const run = async () => {
    if (!parse) return
    setRunning(true)
    const res: RunResult = { clients: 0, reusedClients: 0, employees: 0, assignments: 0, tasks: 0, tasksSkipped: 0, contracts: 0, contacts: 0, errors: [] }
    const nameToId = new Map<string, string>() // person name → employeeId
    for (const emp of employees) nameToId.set(norm(emp.name), emp.id)

    try {
      // 1. Create new employees
      const creates = Object.entries(resolutions).filter(([, r]) => r.mode === 'create') as [string, Extract<Resolution, { mode: 'create' }>][]
      for (const [name, r] of creates) {
        setProgress(`Creating teammate ${name}…`)
        try {
          const emp = await createEmployee({ name, email: r.email, initials: r.initials.toUpperCase(), avatarColor: r.color })
          nameToId.set(norm(name), emp.id)
          res.employees++
        } catch (e) {
          res.errors.push(`Employee "${name}": ${(e as Error).message}`)
        }
      }
      // matched people
      for (const [name, r] of Object.entries(resolutions)) {
        if (r.mode === 'match') nameToId.set(norm(name), r.employeeId)
      }
      const resolve = (name?: string) => (name ? nameToId.get(norm(name)) : undefined)

      // 2. Existing clients by name → id
      const existingClients = new Map((clientsData?.items ?? []).map((c) => [norm(c.name), c.id]))

      // Fresh task-dedup set (client::title) so a re-run doesn't duplicate tasks.
      const seenTasks = new Set<string>()
      try {
        for (const t of (await listTasks()).items) if (t.clientId) seenTasks.add(taskKey(t.clientId, t.title))
      } catch {
        /* best effort — proceed without prior tasks */
      }

      // 3. Per record
      let i = 0
      for (const rec of parse.records) {
        i++
        if (!rec.client.name) continue
        setProgress(`Importing ${rec.client.name} (${i}/${parse.records.length})…`)
        try {
          let clientId = existingClients.get(norm(rec.client.name))
          if (clientId) {
            res.reusedClients++
          } else {
            const c = await createClient({
              name: rec.client.name,
              mrr: rec.client.mrr,
              health: rec.client.health as any,
              notes: rec.client.notes,
            })
            clientId = c.id
            res.clients++
            // Register so a repeated name later in the file reuses this
            // client instead of creating a duplicate.
            existingClients.set(norm(rec.client.name), clientId)
          }

          if (rec.contract) {
            try {
              await createContract({
                clientId,
                name: `${rec.client.name} engagement`,
                term: (rec.contract.term as any) ?? 'monthly',
                status: 'active',
                startDate: rec.contract.startDate ?? undefined,
                mrr: rec.client.mrr,
              })
              res.contracts++
            } catch (e) { res.errors.push(`Contract for ${rec.client.name}: ${(e as Error).message}`) }
          }

          if (rec.poc) {
            try {
              await createContact(clientId, { name: rec.poc.name, email: rec.poc.email, phone: rec.poc.phone, role: 'Main POC' })
              res.contacts++
            } catch (e) { res.errors.push(`Contact for ${rec.client.name}: ${(e as Error).message}`) }
          }

          for (const person of rec.team) {
            const empId = resolve(person.name)
            if (!empId) continue
            try {
              await createAssignment({ employeeId: empId, entityType: 'client', entityId: clientId, role: person.role })
              res.assignments++
            } catch (e) { res.errors.push(`Assign ${person.name} → ${rec.client.name}: ${(e as Error).message}`) }
          }

          for (const tk of rec.tasks) {
            const key = taskKey(clientId, tk.title)
            if (seenTasks.has(key)) { res.tasksSkipped++; continue } // already exists — don't duplicate
            try {
              const task = await createTask({
                title: tk.title,
                clientId,
                priority: tk.priority,
                status: tk.status,
                due: tk.due,
                // The sheet's notes column is the task's detail. It was parsed
                // and then dropped here, so every onboarded task landed blank.
                description: tk.notes || undefined,
              })
              seenTasks.add(key)
              res.tasks++
              const empId = resolve(tk.assignedTo)
              if (empId) {
                await createAssignment({ employeeId: empId, entityType: 'task', entityId: task.id, role: 'lead' })
                res.assignments++
              }
            } catch (e) { res.errors.push(`Task "${tk.title}" (${rec.client.name}): ${(e as Error).message}`) }
          }
        } catch (e) {
          res.errors.push(`${rec.client.name}: ${(e as Error).message}`)
        }
      }
    } finally {
      setRunning(false)
      setProgress('')
      setResult(res)
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['assignments'] })
      refreshRoster(qc)
    }
  }

  const people = parse?.people ?? []

  return (
    <div className="quick-add-backdrop" onClick={() => !running && onClose()}>
      <div className="intake-modal onboard-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Onboard from spreadsheet">
        <div className="intake-header">
          <span className="intake-header-icon"><Users size={18} /></span>
          <div className="intake-header-text">
            <h3>Onboard from spreadsheet</h3>
            <p>Import clients with their team, tasks & terms from a wide export.</p>
          </div>
          <span className="onboard-steps">
            {['Upload', 'People', 'Review'].map((label, i) => (
              <span key={label} className={`onboard-step${step === i + 1 ? ' onboard-step--active' : ''}${step > i + 1 ? ' onboard-step--done' : ''}`}>
                {i + 1}. {label}
              </span>
            ))}
          </span>
          <button onClick={() => !running && onClose()} className="btn-icon intake-close" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="intake-content">
          {/* STEP 1 — Upload */}
          {step === 1 && (
            <>
              <button
                type="button"
                className={`intake-dropzone${dragging ? ' intake-dropzone--active' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f) }}
              >
                <span className="intake-dropzone-icon"><UploadCloud size={26} /></span>
                <span className="intake-dropzone-title">Drop the client spreadsheet or click to browse</span>
                <span className="intake-dropzone-sub">CSV, TSV, or Excel — one row per client (team & tasks inline)</span>
                <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f) }} />
              </button>
              <p className="intake-eyebrow" style={{ marginTop: 'var(--space-2)' }}>or paste rows</p>
              <textarea className="intake-input intake-textarea intake-paste-area" rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the sheet including its header row" />
              {error && <p className="intake-errors intake-errors--block">{error}</p>}
              <div className="intake-footer">
                <span />
                <button type="button" className="btn btn-primary btn-sm" disabled={!text.trim()} onClick={parsePasted}>Parse rows <ArrowRight size={14} /></button>
              </div>
            </>
          )}

          {/* STEP 2 — People */}
          {step === 2 && parse && (
            <>
              <div className="intake-import-stats">
                <span className="intake-stat intake-stat--ok"><CheckCircle2 size={14} /> {parse.records.length} clients · {parse.records.reduce((s, r) => s + r.team.length, 0)} team links · {parse.records.reduce((s, r) => s + r.tasks.length, 0)} tasks</span>
              </div>
              <p className="intake-help-text" style={{ margin: '4px 0 12px' }}>
                <strong>{people.length}</strong> {people.length === 1 ? 'person appears' : 'people appear'} across account managers, team members & task assignees. Match each to a teammate, create them, or skip (their links are dropped).
              </p>
              <div className="onboard-people">
                {people.map((name) => {
                  const r = resolutions[name]
                  if (!r) return null // seeded by effect on the next tick
                  return (
                    <div key={name} className="onboard-person">
                      <span className="onboard-person__name">{name}</span>
                      <select
                        className="assignee-add-select"
                        value={r.mode === 'match' ? `emp:${r.employeeId}` : r.mode}
                        onChange={(e) => {
                          const v = e.target.value
                          setResolutions((p) => ({
                            ...p,
                            [name]: v.startsWith('emp:')
                              ? { mode: 'match', employeeId: v.slice(4) }
                              : v === 'skip'
                                ? { mode: 'skip' }
                                : { mode: 'create', email: '', initials: initialsOf(name), color: COLORS[people.indexOf(name) % COLORS.length]! },
                          }))
                        }}
                      >
                        <option value="create">➕ Create new teammate</option>
                        <option value="skip">Skip (drop links)</option>
                        <optgroup label="Map to existing">
                          {employees.map((e) => <option key={e.id} value={`emp:${e.id}`}>{e.name}</option>)}
                        </optgroup>
                      </select>
                      {r.mode === 'create' && (
                        <span className="onboard-person__create">
                          <input className="assignee-add-select" style={{ width: 60 }} value={r.initials} maxLength={4} onChange={(e) => setResolutions((p) => ({ ...p, [name]: { ...r, initials: e.target.value } }))} aria-label="Initials" />
                          <input className="assignee-add-select" style={{ flex: 1, minWidth: 160 }} type="email" placeholder="email@lxp.com (required)" value={r.email} onChange={(e) => setResolutions((p) => ({ ...p, [name]: { ...r, email: e.target.value } }))} aria-label="Email" />
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className="intake-footer">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setStep(1); setTable(null) }}><ArrowLeft size={14} /> Back</button>
                <button type="button" className="btn btn-primary btn-sm" disabled={createInvalid} onClick={() => setStep(3)}>
                  Review <ArrowRight size={14} />
                </button>
              </div>
              {createInvalid && <p className="intake-errors intake-errors--block">Every "create" teammate needs valid initials and email — fix or switch them to Skip / Map.</p>}
            </>
          )}

          {/* STEP 3 — Review & run */}
          {step === 3 && plan && (
            <>
              {!result ? (
                <>
                  <p className="intake-eyebrow">This import will create</p>
                  <div className="onboard-plan">
                    <PlanTile
                      n={plan.newClients}
                      label="Clients"
                      sub={
                        [
                          plan.reused ? `${plan.reused} already exist → reused` : '',
                          plan.dupRows ? `${plan.dupRows} duplicate row${plan.dupRows === 1 ? '' : 's'} in file → merged` : '',
                        ].filter(Boolean).join(' · ') || undefined
                      }
                    />
                    <PlanTile n={plan.newEmployees} label="New teammates" />
                    <PlanTile n={plan.assignments + plan.taskAssignments} label="Assignments" sub={`${plan.assignments} team · ${plan.taskAssignments} task`} />
                    <PlanTile n={plan.tasks} label="Tasks" sub={plan.dupTasks ? `${plan.dupTasks} already exist → skipped` : undefined} />
                    {plan.contracts > 0 && <PlanTile n={plan.contracts} label="Contracts" />}
                    {plan.contacts > 0 && <PlanTile n={plan.contacts} label="Contacts" />}
                  </div>
                  {plan.skipped.length > 0 && (
                    <div className="intake-banner intake-banner--warn">
                      <AlertTriangle size={15} />
                      <span>Skipping <strong>{plan.skipped.join(', ')}</strong> — their team/task links won't be created.</span>
                    </div>
                  )}
                  <p className="intake-help-text">Existing clients (by name) and tasks (by client + title) are reused, not duplicated — safe to re-run. Contracts and contacts, if any, are re-created each run.</p>
                  <div className="intake-footer">
                    <button type="button" className="btn btn-ghost btn-sm" disabled={running} onClick={() => setStep(2)}><ArrowLeft size={14} /> Back</button>
                    <button type="button" className="btn btn-primary btn-sm" disabled={running} onClick={run}>
                      {running ? (progress || 'Importing…') : 'Run import'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="intake-import-stats"><span className="intake-stat intake-stat--ok"><CheckCircle2 size={16} /> Import complete</span></div>
                  <div className="onboard-plan">
                    <PlanTile n={result.clients} label="Clients created" sub={result.reusedClients ? `${result.reusedClients} reused` : undefined} />
                    <PlanTile n={result.employees} label="Teammates created" />
                    <PlanTile n={result.assignments} label="Assignments" />
                    <PlanTile n={result.tasks} label="Tasks created" sub={result.tasksSkipped ? `${result.tasksSkipped} duplicates skipped` : undefined} />
                    {result.contracts > 0 && <PlanTile n={result.contracts} label="Contracts" />}
                    {result.contacts > 0 && <PlanTile n={result.contacts} label="Contacts" />}
                  </div>
                  {result.errors.length > 0 && (
                    <div className="intake-errors intake-errors--block" style={{ maxHeight: 160, overflow: 'auto' }}>
                      <strong>{result.errors.length} issue{result.errors.length === 1 ? '' : 's'}:</strong>
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{result.errors.slice(0, 30).map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </div>
                  )}
                  <div className="intake-footer">
                    <span />
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => { onSuccess?.(`Onboarded ${result.clients + result.reusedClients} clients`); onClose() }}>Done</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function PlanTile({ n, label, sub }: { n: number; label: string; sub?: string }) {
  return (
    <div className="onboard-plan-tile">
      <span className="onboard-plan-tile__n mono">{n}</span>
      <span className="onboard-plan-tile__label">{label}</span>
      {sub && <span className="onboard-plan-tile__sub">{sub}</span>}
    </div>
  )
}
